import { useActiveOrganization } from "@/hooks/use-organizations";
import type { AdminUser } from "./admin-types";
import { authClient } from "./auth-client";

type SessionUserWithAppAdmin = {
  isAppAdmin?: boolean;
  role?: string | string[];
};

/**
 * Check if the current user has server-confirmed App admin access.
 */
export function useIsSudo() {
  const { data: session } = authClient.useSession();
  const user = session?.user as SessionUserWithAppAdmin | undefined;

  return (
    user?.isAppAdmin === true ||
    (typeof user?.role === "string" && user.role.includes("sudo")) ||
    (Array.isArray(user?.role) && user.role.some((role) => role.includes("sudo")))
  );
}

export function useIsAdmin() {
  return useIsSudo();
}

/**
 * Check if the current user is an owner or admin in the active organization
 */
export function useIsOrgAdmin() {
  const { data: activeOrg } = useActiveOrganization();
  const { data: session } = authClient.useSession();
  const userId = session?.user?.id;

  if (!userId) return false;

  const members = (activeOrg as Record<string, unknown>)?.members as
    | Array<{ userId: string; role: string }>
    | undefined;

  if (!members) return false;

  const myMember = members.find((m) => m.userId === userId);
  return myMember?.role === "owner" || myMember?.role === "admin";
}

/**
 * Get user role(s) as an array
 */
export function getUserRoles(user: AdminUser | undefined): string[] {
  if (!user?.role) {
    return ["user"];
  }

  if (typeof user.role === "string") {
    return user.role.split(",").map((r) => r.trim());
  }

  return Array.isArray(user.role) ? user.role : ["user"];
}

const APP_USER_ROLE_LABEL_KEYS = {
  user: "admin.user.form.user",
  sudo: "admin.user.form.admin",
} as const;

export function getUserRoleLabelKey(role: string): string | undefined {
  return APP_USER_ROLE_LABEL_KEYS[role as keyof typeof APP_USER_ROLE_LABEL_KEYS];
}

/**
 * Check if user has a specific role
 */
export function hasRole(user: AdminUser | undefined, role: string): boolean {
  const roles = getUserRoles(user);
  return roles.includes(role);
}

/**
 * Format user status for display
 */
export function getUserStatus(user: AdminUser): {
  status: "active" | "banned" | "unverified";
  label: string;
  variant: "default" | "destructive" | "secondary";
} {
  if (user.banned) {
    return {
      status: "banned",
      label:
        user.banExpires && new Date(user.banExpires) > new Date() ? "Temporarily Banned" : "Banned",
      variant: "destructive",
    };
  }

  if (!user.emailVerified) {
    return {
      status: "unverified",
      label: "Email Unverified",
      variant: "secondary",
    };
  }

  return {
    status: "active",
    label: "Active",
    variant: "default",
  };
}

/**
 * Format date for display
 */
export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Time calculation constants
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const MILLISECONDS_PER_MINUTE = MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE;
const MILLISECONDS_PER_HOUR = MILLISECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const MILLISECONDS_PER_DAY = MILLISECONDS_PER_HOUR * HOURS_PER_DAY;

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / MILLISECONDS_PER_DAY);
  const diffHours = Math.floor(diffMs / MILLISECONDS_PER_HOUR);
  const diffMinutes = Math.floor(diffMs / MILLISECONDS_PER_MINUTE);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }
  return "Just now";
}
